import { supabase } from "@/integrations/supabase/client";

const RESOURCES_BUCKET = "agent-resources";

export interface UploadedResourceData {
  /** Permanent public URL — safe to persist and share long-term. */
  fileUrl: string;
  storagePath: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
}

/**
 * Uploads a resource document to the public `agent-resources` bucket and
 * returns a permanent public URL (not a signed URL), so a shared agent link
 * keeps working long after the document was added.
 *
 * @param file The file to upload
 * @param agentId Used to namespace the storage path per Genie
 */
export async function uploadResourceToStorage(
  file: File,
  agentId: string,
): Promise<UploadedResourceData> {
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const folder = agentId ? agentId.replace(/[^a-zA-Z0-9._-]/g, "_") : "shared";
  const storagePath = `${folder}/${Date.now()}_${sanitizedFileName}`;

  const { error: uploadError } = await supabase.storage
    .from(RESOURCES_BUCKET)
    .upload(storagePath, file, { upsert: false });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage.from(RESOURCES_BUCKET).getPublicUrl(storagePath);
  const fileUrl = urlData?.publicUrl;
  if (!fileUrl) throw new Error("Failed to generate public URL");

  return {
    fileUrl,
    storagePath,
    fileName: file.name,
    mimeType: file.type || undefined,
    sizeBytes: file.size,
  };
}

/** Removes a previously uploaded resource document from storage. */
export async function removeResourceFromStorage(storagePath: string): Promise<void> {
  if (!storagePath) return;
  const { error } = await supabase.storage.from(RESOURCES_BUCKET).remove([storagePath]);
  if (error) throw error;
}
